import { ChangeEvent, HTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import emojiRegex from 'emoji-regex';
import { PopoverPlacement, PopoverTriggerType } from '@idui/react-popover';
import { MessageMedia } from 'utils/gloo-types';
import { MediaUploadDto } from 'utils/mms';
import { deleteCloudinaryMedia } from 'actions/requests';
import { canOrgDoMMS } from 'utils/index';
import { useGetTextReplacers, useOrg, useThunkDispatch } from 'utils/reduxHook';
import { IconName } from 'components/atoms/Icon/all';
import Icon from 'components/atoms/Icon';
import { CloudinaryMediaUploader, MediaUploadResult } from 'components/organisms/CloudinaryMediaUploader';
import Button from 'components/atoms/Button';
import HelpPopover from 'components/molecules/HelpPopover';
import { MMSImage } from 'components/molecules/MMSImage';
import MoreOptionsDropDown from 'components/molecules/MoreOptionsDropDown';
import ShortenUrlButton from 'components/molecules/ShortenUrlButton';
import Typography from 'components/atoms/Typography';
import EmojiPopover from '../EmojiPopover';

/* eslint-disable react/no-unstable-nested-components */
export interface MMSMediaInputProps extends HTMLAttributes<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement> {
  autoFocus?: boolean;
  characterLimit?: number;
  containerStyles?: string;
  disabled?: boolean;
  emojiCharacterLimit?: number;
  error?: string;
  icon?: IconName;
  isAutoGrow?: boolean;
  isClearable?: boolean;
  label?: string | ReactNode;
  onChangeText?: (t: string, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>) => any;
  required?: boolean;
  step?: string | number;
  value?: string | number;
  onShortenUrl?: (fullText: string) => any;
  onMediaUpload?: (mediaRef: MediaUploadResult) => any;
  onAddUserData?: (replacer: string) => any;
  cloudinaryImage?: MediaUploadResult;
  removeImageFromLocalState?: () => void;
  placeholder?: string;
  mediaUploadDto?: MediaUploadDto;
  textReplacerType?: string;
  messageMedia?: MessageMedia;
}

export const MMSMediaInput = ({
  characterLimit,
  className,
  containerStyles,
  defaultValue,
  disabled,
  emojiCharacterLimit,
  error,
  icon,
  isClearable,
  label,
  onChange,
  onChangeText,
  required,
  value,
  cloudinaryImage,
  onShortenUrl,
  onMediaUpload,
  onAddUserData,
  removeImageFromLocalState,
  placeholder,
  mediaUploadDto,
  textReplacerType,
  messageMedia,
  ...rest
}: MMSMediaInputProps) => {
  const [hasEmoji, setHasEmoji] = useState(false);
  const [isOverLimit, setIsOverLimit] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue || '');
  const [videoUrl, setVideoUrl] = useState({ url: '', linkInText: '' });

  const charLimit = emojiCharacterLimit && hasEmoji ? emojiCharacterLimit : characterLimit;
  const displayCharLimit = !!charLimit;
  const valueStr = !onChangeText && !onChange ? (internalValue as string) : (value || '').toString();
  const organization = useOrg();
  const isMmsAllowed = canOrgDoMMS(organization);
  const dispatch = useThunkDispatch();
  const mediaInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (value && typeof value === 'string') mediaInputRef.current.value = value;
  }, [organization]);

  const deleteImage = async (id: string | null) => {
    if (removeImageFromLocalState) removeImageFromLocalState();
    if (id) {
      await dispatch(deleteCloudinaryMedia([id]));
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value || '';
    const newHasEmoji = emojiCharacterLimit && emojiRegex().test(text);

    if (emojiCharacterLimit && newHasEmoji !== hasEmoji) {
      setHasEmoji(newHasEmoji);
    }

    if ((characterLimit && text.length > characterLimit) || (newHasEmoji && text.length > emojiCharacterLimit)) {
      if (isOverLimit === false) {
        setIsOverLimit(true);
      }
    } else if (
      isOverLimit &&
      ((characterLimit && text.length <= characterLimit) || (newHasEmoji && text.length <= emojiCharacterLimit))
    ) {
      setIsOverLimit(false);
    }

    if (onChangeText) {
      onChangeText(text, e);
    } else if (onChange) {
      onChange(e);
    } else {
      setInternalValue(text);
    }

    // Remove the video preview if the user deletes the video link
    if (videoUrl.url && !text.includes(videoUrl.linkInText)) {
      setVideoUrl({ url: '', linkInText: '' });
    }
  };

  const textReplacers = useGetTextReplacers(textReplacerType);

  const handleAddUserData = (replacer: string) => {
    const [start, end] = [mediaInputRef.current.selectionStart, mediaInputRef.current.selectionEnd];
    let replacerText = '';
    if (!/\s+$/.test(mediaInputRef.current.value)) {
      replacerText = ` ${replacer}`;
    } else {
      replacerText = replacer;
    }

    mediaInputRef.current.setRangeText(replacerText, start, end, 'select');

    if (textReplacerType === 'Conversation Prompts') {
      onAddUserData(mediaInputRef.current.value);
    } else {
      onAddUserData(replacerText);
    }
  };

  const handleMediaUpload = (mediaRef: MediaUploadResult) => {
    if (onMediaUpload) {
      onMediaUpload(mediaRef);
    }
  };

  const insertEmoji = (emoji) => {
    const [start, end] = [mediaInputRef.current.selectionStart, mediaInputRef.current.selectionEnd];

    mediaInputRef.current.setRangeText(emoji, start, end, 'select');
  };

  const hasImage = cloudinaryImage?.info !== undefined || mediaUploadDto?.info !== undefined || !!messageMedia;

  return (
    <>
      <div
        className={classNames(
          'flex flex-col items-start justify-center w-full focus-within:border-charcoal-600 bg-white text-sm border-glooGrayBorder border rounded-t-sm p-2 md:p-4 md:py-2',
          { 'opacity-50': disabled },
          { 'border-red border': !!error },
          { relative: isClearable },
          containerStyles,
        )}
      >
        {icon ? <Icon name={icon} className="ml-2 absolute z-10 text-charcoal-400" size={18} /> : null}
        {label && (
          <Typography variant="label" className="pb-2 pl-1">
            {label}
            {required && <span className="text-red text-xs">*</span>}
          </Typography>
        )}
        <div className={classNames('flex items-center cursor-default pb-0')}>
          {displayCharLimit ? (
            <div
              className={classNames('text-xs font-bold mr-4 text-glooLightGray', {
                'text-red': isOverLimit,
              })}
            >
              {hasEmoji && emojiCharacterLimit && (
                <span className="text-yellow mr-2">Emojis change your character limit</span>
              )}
              {(mediaInputRef?.current?.value || valueStr || '').length}/{charLimit}
            </div>
          ) : null}
        </div>
        <div className={classNames('flex flex-col w-full relative')}>
          <textarea
            placeholder={placeholder || 'Send a message'}
            ref={mediaInputRef}
            data-durable-id="inputTextAreaLinkId"
            data-intercom-target="inputTextAreaLinkId"
            defaultValue={defaultValue}
            {...rest}
            className={classNames(
              'appearance-none w-full text-glooCharcoal leading-tight break-words focus:outline-none text-sm editable focus:border-skyBlue-600 cursor-text border-glooGrayBorder py-1 min-h-[5rem] resize-none',
              { 'pr-26': hasImage },
              className,
            )}
            onInput={handleChange as any}
          />
          {hasImage ? (
            <MMSImage
              image={cloudinaryImage?.info ? cloudinaryImage : mediaUploadDto}
              messageMedia={messageMedia}
              deleteImage={deleteImage}
            />
          ) : null}
          {isClearable && valueStr.length > 0 ? (
            <div
              className="absolute top-0 bottom-0 right-0 flex flex-row items-start p-2 pt-3 cursor-pointer animate100 animated fadeInRight"
              onClick={() => handleChange({ target: { value: '' } } as any)}
            >
              <Icon name="x" className="text-glooMedGray" size={18} />
            </div>
          ) : null}
          {error ? <div className="text-xs text-red p-2">{error}</div> : null}
        </div>
      </div>

      <div
        className={classNames(
          'flex flex-row items-center justify-start w-full py-1 bg-white text-sm border-glooGrayBorder border-l border-r border-b rounded-b-sm',
          'pl-2 pr-2 pt-3 pb-3',
          { relative: isClearable },
          containerStyles,
        )}
      >
        <div className="gap-4 flex flex-row">
          {onShortenUrl && <ShortenUrlButton value={value} onShortenUrl={onShortenUrl} />}
          {onAddUserData && (
            <HelpPopover
              inline
              flex
              fitMaxWidthToBounds
              mobilePlacement={PopoverPlacement.right}
              content={textReplacerType === 'Conversation Prompts' ? 'Insert Dynamic Field' : 'Add User Data'}
            >
              <MoreOptionsDropDown
                width={280}
                cardClassName="max-h-72"
                popoverClassName="!pr-4"
                buttons={textReplacers.map(({ text, replacer }) => ({
                  text,
                  dataDurableId: 'userDataOptionButton',
                  size: 'small',
                  className: 'text-xs',
                  onClick: () => handleAddUserData(replacer),
                }))}
                placement={
                  textReplacerType === 'Conversation Prompts' ? PopoverPlacement.topLeft : PopoverPlacement.top
                }
                mobilePlacement={PopoverPlacement.topLeft}
                content={
                  <Button
                    className="text-glooMedGray hover:text-skyBlue-600"
                    data-analytics="input-insert-data"
                    type="transparent"
                    dataDurableId="insertDataButton"
                    icon="insert-user-data"
                    iconSize={20}
                  />
                }
              />
            </HelpPopover>
          )}
          {isMmsAllowed && (
            <CloudinaryMediaUploader
              icon="image"
              iconSize={20}
              className="text-glooMedGray hover:text-skyBlue-600"
              onMediaUpload={handleMediaUpload}
              uploaderOptions={{ clientAllowedFormats: ['png', 'jpeg', 'bmp', 'gif'], maxFileSize: 525000 }}
              disabled={hasImage}
            />
          )}
          <EmojiPopover
            icon="smile"
            width="24"
            height="24"
            trigger={PopoverTriggerType.click}
            flex
            iconClassName="text-glooMedGray"
            arrowColor="#FFF"
            closeOnRemoteClick
            onEmojiClick={(emojiData) => {
              // hack for broadcasts & series
              // onEmojiSelect(emojiData);
              insertEmoji(emojiData.emoji);
              handleChange({ target: { value: `${mediaInputRef.current.value}` } } as any);
            }}
          />
        </div>
      </div>
    </>
  );
};
